/**
 * Playlist Service
 * CRUD for user playlists — all users can create playlists from any media,
 * content creators can also filter to only their own media.
 */

import { db } from '@/lib/firebase';
import {
  collection, doc, addDoc, getDoc, getDocs, updateDoc, deleteDoc,
  query, where, orderBy, limit, onSnapshot, serverTimestamp, Timestamp,
  arrayUnion, arrayRemove, writeBatch,
} from 'firebase/firestore';

// ============ TYPES ============

export interface PlaylistTrack {
  trackId: string;       // reference to work/release track
  title: string;
  artist: string;
  imageUrl?: string;
  audioUrl?: string;
  youtubeId?: string;
  duration?: number;     // seconds
  addedAt?: any;
  addedBy?: string;      // userId who added this track
  order: number;
}

export interface Playlist {
  id: string;
  title: string;
  description?: string;
  coverImageUrl?: string;
  /** Every added track's artwork — the playlist thumbnail is a slideshow of these (Gordon 2026-11-02). */
  coverImages?: string[];
  creatorId: string;       // userId
  creatorName: string;
  creatorAvatar?: string;
  visibility: 'public' | 'private' | 'unlisted';
  mediaFilter: 'all' | 'own';  // 'own' = only creator's media (for content creators)
  tags?: string[];
  genre?: string;
  trackCount: number;
  totalDuration?: number;  // seconds
  likesCount: number;
  playsCount: number;
  createdAt: any;
  updatedAt: any;
}

// ============ CRUD ============

const PLAYLISTS_COL = 'playlists';
const PLAYLIST_TRACKS_COL = 'playlistTracks';

/**
 * Create a new playlist
 */
export async function createPlaylist(data: {
  title: string;
  description?: string;
  coverImageUrl?: string;
  creatorId: string;
  creatorName: string;
  creatorAvatar?: string;
  visibility?: 'public' | 'private' | 'unlisted';
  mediaFilter?: 'all' | 'own';
  tags?: string[];
  genre?: string;
}): Promise<string> {
  const docRef = await addDoc(collection(db, PLAYLISTS_COL), {
    title: data.title,
    description: data.description || '',
    coverImageUrl: data.coverImageUrl || '',
    creatorId: data.creatorId,
    creatorName: data.creatorName,
    creatorAvatar: data.creatorAvatar || '',
    visibility: data.visibility || 'private',
    mediaFilter: data.mediaFilter || 'all',
    tags: data.tags || [],
    genre: data.genre || '',
    trackCount: 0,
    totalDuration: 0,
    likesCount: 0,
    playsCount: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
}

/**
 * Get a single playlist by ID
 */
export async function getPlaylist(playlistId: string): Promise<Playlist | null> {
  const snap = await getDoc(doc(db, PLAYLISTS_COL, playlistId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as Playlist;
}

/**
 * Get all playlists for a user
 */
export async function getUserPlaylists(userId: string): Promise<Playlist[]> {
  try {
    const q = query(
      collection(db, PLAYLISTS_COL),
      where('creatorId', '==', userId),
      orderBy('updatedAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as Playlist));
  } catch (error) {
    console.error('Error fetching playlists (trying fallback without orderBy):', error);
    try {
      const fallbackQ = query(
        collection(db, PLAYLISTS_COL),
        where('creatorId', '==', userId)
      );
      const snap = await getDocs(fallbackQ);
      return snap.docs
        .map(d => ({ id: d.id, ...d.data() } as Playlist))
        .sort((a, b) => {
          const aTime = a.updatedAt?.toMillis?.() || a.createdAt?.toMillis?.() || 0;
          const bTime = b.updatedAt?.toMillis?.() || b.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
        });
    } catch (err2) {
      console.error('Error fetching playlists (fallback):', err2);
      return [];
    }
  }
}

/**
 * Get ALL playlists (super admin only — no visibility filter)
 */
export async function getAllPlaylists(): Promise<Playlist[]> {
  try {
    const q = query(
      collection(db, PLAYLISTS_COL),
      orderBy('updatedAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as Playlist));
  } catch (error) {
    console.error('Error fetching all playlists:', error);
    try {
      const snap = await getDocs(collection(db, PLAYLISTS_COL));
      return snap.docs.map(d => ({ id: d.id, ...d.data() } as Playlist));
    } catch (err2) {
      console.error('Error fetching all playlists (fallback):', err2);
      return [];
    }
  }
}

/**
 * Get public playlists (for discovery)
 */
export async function getPublicPlaylists(limitCount = 20): Promise<Playlist[]> {
  try {
    const q = query(
      collection(db, PLAYLISTS_COL),
      where('visibility', '==', 'public'),
      orderBy('likesCount', 'desc'),
      limit(limitCount)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as Playlist));
  } catch (error) {
    console.error('Error fetching public playlists (trying fallback):', error);
    try {
      const fallbackQ = query(
        collection(db, PLAYLISTS_COL),
        where('visibility', '==', 'public')
      );
      const snap = await getDocs(fallbackQ);
      return snap.docs
        .map(d => ({ id: d.id, ...d.data() } as Playlist))
        .sort((a, b) => (b.likesCount || 0) - (a.likesCount || 0))
        .slice(0, limitCount);
    } catch (err2) {
      console.error('Error fetching public playlists (fallback):', err2);
      return [];
    }
  }
}

/**
 * Update playlist metadata
 */
export async function updatePlaylist(
  playlistId: string,
  updates: Partial<Pick<Playlist, 'title' | 'description' | 'coverImageUrl' | 'visibility' | 'mediaFilter' | 'tags' | 'genre'>>
): Promise<void> {
  await updateDoc(doc(db, PLAYLISTS_COL, playlistId), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Delete a playlist and all its tracks
 */
export async function deletePlaylist(playlistId: string): Promise<void> {
  // Delete all tracks in the playlist first
  const tracksQ = query(collection(db, PLAYLIST_TRACKS_COL), where('playlistId', '==', playlistId));
  const tracksSnap = await getDocs(tracksQ);
  const batch = writeBatch(db);
  tracksSnap.docs.forEach(d => batch.delete(d.ref));
  batch.delete(doc(db, PLAYLISTS_COL, playlistId));
  await batch.commit();
}

// ============ TRACKS ============

/**
 * Add a track to a playlist
 */
export async function addTrackToPlaylist(
  playlistId: string,
  track: Omit<PlaylistTrack, 'order' | 'addedAt'>
): Promise<string> {
  // Get current track count for ordering
  const tracksQ = query(collection(db, PLAYLIST_TRACKS_COL), where('playlistId', '==', playlistId));
  const snap = await getDocs(tracksQ);
  const order = snap.size;

  // Strip undefined values — Firestore rejects any field set to undefined
  const safeTrack: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(track)) {
    if (v !== undefined) safeTrack[k] = v;
  }

  const docRef = await addDoc(collection(db, PLAYLIST_TRACKS_COL), {
    playlistId,
    ...safeTrack,
    order,
    addedAt: serverTimestamp(),
  });

  // Update playlist metadata
  await updateDoc(doc(db, PLAYLISTS_COL, playlistId), {
    trackCount: order + 1,
    totalDuration: (snap.docs.reduce((sum, d) => sum + (d.data().duration || 0), 0) + (track.duration || 0)),
    updatedAt: serverTimestamp(),
    // Set cover image from first track if not set
    ...(order === 0 && track.imageUrl ? { coverImageUrl: track.imageUrl } : {}),
    // …and every track's artwork joins the playlist's thumbnail slideshow.
    ...(track.imageUrl ? { coverImages: arrayUnion(track.imageUrl) } : {}),
  });

  // The track's owner hears about it in chat (fire-and-forget).
  if (track.addedBy && track.trackId) {
    void getDoc(doc(db, PLAYLISTS_COL, playlistId)).then(p => {
      const name = (p.exists() && (p.data() as any).name) || '';
      return import('@/services/track-notices').then(m => m.notifyTrackOwner({
        trackId: track.trackId,
        actor: { userId: track.addedBy! },
        text: `➕🎶 "${track.title}"${name ? ` → ${name}` : ''}`,
        subtitle: name || undefined,
      }));
    }).catch(() => {});
  }

  return docRef.id;
}

/**
 * Remove a track from a playlist
 */
export async function removeTrackFromPlaylist(playlistId: string, trackDocId: string): Promise<void> {
  await deleteDoc(doc(db, PLAYLIST_TRACKS_COL, trackDocId));

  // Re-count tracks
  const tracksQ = query(collection(db, PLAYLIST_TRACKS_COL), where('playlistId', '==', playlistId));
  const snap = await getDocs(tracksQ);
  await updateDoc(doc(db, PLAYLISTS_COL, playlistId), {
    trackCount: snap.size,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Get tracks for a playlist (ordered)
 */
export async function getPlaylistTracks(playlistId: string): Promise<(PlaylistTrack & { docId: string })[]> {
  try {
    const q = query(
      collection(db, PLAYLIST_TRACKS_COL),
      where('playlistId', '==', playlistId),
      orderBy('order', 'asc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ docId: d.id, ...d.data() } as PlaylistTrack & { docId: string }));
  } catch (error) {
    console.error('Error fetching playlist tracks (trying fallback without orderBy):', error);
    try {
      const fallbackQ = query(
        collection(db, PLAYLIST_TRACKS_COL),
        where('playlistId', '==', playlistId)
      );
      const snap = await getDocs(fallbackQ);
      return snap.docs
        .map(d => ({ docId: d.id, ...d.data() } as PlaylistTrack & { docId: string }))
        .sort((a, b) => (a.order || 0) - (b.order || 0));
    } catch (err2) {
      console.error('Error fetching playlist tracks (fallback):', err2);
      return [];
    }
  }
}

/**
 * Reorder tracks in a playlist
 */
export async function reorderPlaylistTracks(orderedDocIds: string[]): Promise<void> {
  const batch = writeBatch(db);
  orderedDocIds.forEach((id, index) => {
    batch.update(doc(db, PLAYLIST_TRACKS_COL, id), { order: index });
  });
  await batch.commit();
}

/**
 * Subscribe to a user's playlists (real-time)
 */
export function subscribeUserPlaylists(
  userId: string,
  callback: (playlists: Playlist[]) => void
): () => void {
  const q = query(
    collection(db, PLAYLISTS_COL),
    where('creatorId', '==', userId),
    orderBy('updatedAt', 'desc')
  );
  let fallbackUnsub: (() => void) | null = null;
  const unsub = onSnapshot(q, snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() } as Playlist)));
  }, (error) => {
    console.error('Error subscribing to playlists (trying fallback):', error);
    const fallbackQ = query(
      collection(db, PLAYLISTS_COL),
      where('creatorId', '==', userId)
    );
    fallbackUnsub = onSnapshot(fallbackQ, snap => {
      const playlists = snap.docs
        .map(d => ({ id: d.id, ...d.data() } as Playlist))
        .sort((a, b) => {
          const aTime = a.updatedAt?.toMillis?.() || a.createdAt?.toMillis?.() || 0;
          const bTime = b.updatedAt?.toMillis?.() || b.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
        });
      callback(playlists);
    }, (err2) => {
      console.error('Error subscribing to playlists (fallback):', err2);
      callback([]);
    });
  });
  return () => { unsub(); fallbackUnsub?.(); };
}

/**
 * Subscribe to playlist tracks (real-time)
 */
export function subscribePlaylistTracks(
  playlistId: string,
  callback: (tracks: (PlaylistTrack & { docId: string })[]) => void
): () => void {
  const q = query(
    collection(db, PLAYLIST_TRACKS_COL),
    where('playlistId', '==', playlistId),
    orderBy('order', 'asc')
  );
  let fallbackUnsub: (() => void) | null = null;
  const unsub = onSnapshot(q, snap => {
    callback(snap.docs.map(d => ({ docId: d.id, ...d.data() } as PlaylistTrack & { docId: string })));
  }, (error) => {
    console.error('Error subscribing to playlist tracks (trying fallback):', error);
    const fallbackQ = query(
      collection(db, PLAYLIST_TRACKS_COL),
      where('playlistId', '==', playlistId)
    );
    fallbackUnsub = onSnapshot(fallbackQ, snap => {
      const tracks = snap.docs
        .map(d => ({ docId: d.id, ...d.data() } as PlaylistTrack & { docId: string }))
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      callback(tracks);
    }, (err2) => {
      console.error('Error subscribing to playlist tracks (fallback):', err2);
      callback([]);
    });
  });
  return () => { unsub(); fallbackUnsub?.(); };
}

/**
 * Increment play count
 */
export async function incrementPlaylistPlays(playlistId: string): Promise<void> {
  const ref = doc(db, PLAYLISTS_COL, playlistId);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await updateDoc(ref, { playsCount: (snap.data().playsCount || 0) + 1 });
  }
}
